import React, { useEffect, useRef } from 'react'
import tw, { styled } from 'twin.macro'
import { useCleanWorkoutMutation, useListenWorkoutQuery } from '../programApi'
import Workout from 'modules/Programs/components/Workout'
import { DayHeader } from './DayHeader'
import { Skeleton } from 'common/components/Skeleton/Skeleton'
import { DayHeaderContainer } from './styles'
import { RefsControlProvider } from 'common/components/RefsControl/WorkoutRefsControl/context'
import { titleDescMinHeight, headerMinHeight, dayViewMinHeight } from '../constants/dayViewStyleConstants'
import { useEventListener } from 'common/hooks/useEventListener'
import { weekRenderTypes } from '../programRenderingSlice'
import { useMonitorCurrWktDragging } from '../hooks/useMonitorCurrWktDragging'
import { getDayCardWidth } from '../utils/dayViewUtils'
import { EmptyDayCard } from './EmptyDayCard'

const Container = styled.div(({ isEditing }) => [
  tw`relative flex flex-col bg-white border-l border-r h-full`,
  !isEditing && tw`hover:cursor-pointer`,
  isEditing ? tw`border border-tGray-ml` : tw`border-gray-200`,
])

const Day = (props) => {
  const {
    setEditIdx,
    dayIdx,
    weekIdx,
    workoutId,
    workoutIds,
    attributes,
    listeners,
    orgId,
    isEditing,
    isEditingSameWk,
    weekWidth,
    setModalWorkout,
    setEditWorkoutInfoModalOpen,
    switchToMultiWkts,
    selectedWktId,
    setSelectedWktId,
  } = props

  const cardW = getDayCardWidth({
    isEditing,
    isEditingSameWk,
    weekWidth,
  })

  const [cleanWorkout] = useCleanWorkoutMutation()

  const {
    data: workout = {},
    isLoading,
    isFetching,
  } = useListenWorkoutQuery({
    orgId,
    workoutId,
  })

  // When deleting week, workouts have only ids and parts
  const weekDeleting = workout.title === undefined && workout.dayIdx === undefined && workout.type === undefined
  const hasLoaded = !isLoading && !isFetching && !workout.stillLoading && !weekDeleting

  const clearEditIdxOnEsc = (e) => {
    if (isEditing && e.key === 'Escape') {
      setEditIdx(null)
    }
  }

  useEventListener('keydown', clearEditIdxOnEsc)

  const isWktDragging = useMonitorCurrWktDragging({ workoutId })

  const isMounted = useRef(false)
  useEffect(() => {
    if (!isMounted.current) {
      //prevent calling on mount
      isMounted.current = true
    } else if (!isEditing && !isWktDragging) {
      cleanWorkout({
        orgId,
        workoutId: workout.id,
        workout,
      })
      // deselect multiWkt when stopping editing of empty wkt for better UX
      if (workoutIds.length > 1 && workout?.type === 'empty') {
        setSelectedWktId(null)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing])

  const titleRef = useRef()

  if (hasLoaded && props.renderType === weekRenderTypes.show) {
    return (
      <Container
        data-testid='dayContainer'
        className='group'
        style={{ width: cardW, minHeight: dayViewMinHeight }}
        isEditing={isEditing}
        onClick={(e) => {
          if (!isEditing) {
            if (workout.type === 'workout' && e.target.tagName !== 'INPUT' && e.target.tagName !== 'TEXTAREA') {
              titleRef.current.focus()
            }
          }
        }}
      >
        {workout ? (
          <>
            <DayHeader
              dayIdx={dayIdx}
              setEditIdx={setEditIdx}
              workout={workout}
              isEditing={isEditing}
              dragHandleProps={{ attributes, listeners }}
              orgId={orgId}
              setModalWorkout={setModalWorkout}
              setEditWorkoutInfoModalOpen={setEditWorkoutInfoModalOpen}
              switchToMultiWkts={switchToMultiWkts}
              workoutIds={workoutIds}
              mainWktId={workout?.id}
              selectedWktId={selectedWktId}
              setSelectedWktId={setSelectedWktId}
            />
            {workout.type === 'empty' ? (
              <EmptyDayCard
                workout={workout}
                selectedWktId={selectedWktId}
                orgId={orgId}
                setEditIdx={setEditIdx}
                dayIdx={dayIdx}
              />
            ) : (
              <RefsControlProvider isEditing={isEditing}>
                <Workout
                  setEditIdx={setEditIdx}
                  dayIdx={dayIdx}
                  weekIdx={weekIdx}
                  workout={workout}
                  isEditing={isEditing}
                  orgId={orgId}
                  titleRef={titleRef}
                  selectedWktId={selectedWktId}
                />
              </RefsControlProvider>
            )}
          </>
        ) : null}
      </Container>
    )
  } else {
    return (
      <Container style={{ width: cardW, minHeight: dayViewMinHeight }}>
        <DayHeaderContainer style={{ minHeight: headerMinHeight }}>
          <Skeleton css={tw`w-10 mt-2`} />
        </DayHeaderContainer>
        <div
          className='flex flex-col justify-center p-4 border-b border-gray-200'
          style={{ minHeight: titleDescMinHeight }}
        >
          <Skeleton css={tw`w-3/6 mb-6`} />
          <Skeleton css={tw`w-5/6`} />
        </div>
        <div className='h-full' />
      </Container>
    )
  }
}

function areEqual(prevProps, nextProps) {
  const propKeys = Object.keys(nextProps)
  const propsChanged = []

  propKeys.forEach((key) => {
    const isSame = prevProps[key] === nextProps[key]
    if (!isSame) {
      propsChanged.push(key)
    }
  })

  if (propsChanged.length) {
    // console.log('===== DayView.js dayIdx:', nextProps.dayIdx, '===== ')
    // console.log('propsChanged', propsChanged)

    return false
  } else {
    return true
  }
}

export const DayView = React.memo(Day, areEqual)
